/**
 * LIVE AUDIO: the frames a person listens to, and talks through, while a call
 * is in progress.
 *
 * WHY THIS EXISTS NEXT TO THE RECORDING FRAMES AND IS NOT THEM. The recording
 * path (`encodeFrame` in index.ts) is built for custody: 2-second chunks,
 * durable acks, resume, a CRC, the wire-exact Opus where the browser allows it.
 * All of that is latency. A person listening in needs the opposite — audio
 * within a few hundred milliseconds, and a lost frame is a click, not a gap in
 * the record. So live audio has its own tiny frame, its own socket (`/ws/live`
 * for the caller, `/ws/monitor` for the person), and is never written to the
 * recording by the browser.
 *
 * THE FRAME. 8-byte header, then PCM16 little-endian mono at LIVE_SAMPLE_RATE:
 *
 *    off len  field
 *      0   1  version     LIVE_FRAME_VERSION
 *      1   1  source      LiveSource (who is speaking)
 *      2   4  seq         uint32 LE, per source, +1 per frame
 *      6   2  reserved    zero
 *      8   n  payload     PCM16LE, n/2 samples, at most LIVE_MAX_FRAME_SAMPLES
 *
 * One format for every leg, whatever produced it: the caller's browser (48 kHz
 * tapped and decimated), the person's console, or Twilio's 8 kHz mu-law
 * (converted by the Durable Object, which is why the codecs below live here).
 * The console and the caller's browser therefore only ever handle one kind of
 * audio, and only the DO knows telephony exists.
 *
 * 16 kHz mono PCM is 256 kbps per voice. Chosen over Opus because every
 * browser can produce and play it with no encoder, and the only time two
 * voices flow at once is while somebody is actually listening.
 */

export const LIVE_FRAME_VERSION = 1 as const;
export const LIVE_FRAME_HEADER_BYTES = 8;
export const LIVE_SAMPLE_RATE = 16_000;
/** 200 ms. Frames are normally 20-40 ms; anything bigger is refused. */
export const LIVE_MAX_FRAME_SAMPLES = 3_200;

/** Who a frame's audio belongs to. */
export const LiveSource = { Caller: 1, Agent: 2, Human: 3 } as const;
export type LiveSource = (typeof LiveSource)[keyof typeof LiveSource];

export const LIVE_SOURCE_NAME: Record<LiveSource, 'caller' | 'agent' | 'human'> = {
  [LiveSource.Caller]: 'caller',
  [LiveSource.Agent]: 'agent',
  [LiveSource.Human]: 'human',
};

export class LiveFrameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LiveFrameError';
  }
}

function isLiveSource(n: number): n is LiveSource {
  return n === LiveSource.Caller || n === LiveSource.Agent || n === LiveSource.Human;
}

export function encodeLiveFrame(source: LiveSource, seq: number, pcm: Int16Array): ArrayBuffer {
  if (pcm.length > LIVE_MAX_FRAME_SAMPLES) {
    throw new LiveFrameError(`frame too long: ${pcm.length} samples (max ${LIVE_MAX_FRAME_SAMPLES})`);
  }
  const buf = new ArrayBuffer(LIVE_FRAME_HEADER_BYTES + pcm.length * 2);
  const view = new DataView(buf);
  view.setUint8(0, LIVE_FRAME_VERSION);
  view.setUint8(1, source);
  view.setUint32(2, seq >>> 0, true);
  view.setUint16(6, 0, true);
  // DataView per sample rather than an Int16Array copy, so the payload is
  // little-endian by construction and not by the host's byte order.
  for (let i = 0; i < pcm.length; i++) view.setInt16(LIVE_FRAME_HEADER_BYTES + i * 2, pcm[i]!, true);
  return buf;
}

export interface LiveFrame {
  source: LiveSource;
  seq: number;
  pcm: Int16Array;
}

export function decodeLiveFrame(buf: ArrayBuffer): LiveFrame {
  if (buf.byteLength < LIVE_FRAME_HEADER_BYTES) throw new LiveFrameError(`short frame: ${buf.byteLength} bytes`);
  const view = new DataView(buf);
  const version = view.getUint8(0);
  if (version !== LIVE_FRAME_VERSION) throw new LiveFrameError(`unknown live frame version ${version}`);
  const source = view.getUint8(1);
  if (!isLiveSource(source)) throw new LiveFrameError(`unknown live source ${source}`);
  const payloadBytes = buf.byteLength - LIVE_FRAME_HEADER_BYTES;
  if (payloadBytes % 2 !== 0) throw new LiveFrameError(`odd payload length ${payloadBytes}`);
  const samples = payloadBytes / 2;
  if (samples > LIVE_MAX_FRAME_SAMPLES) throw new LiveFrameError(`frame too long: ${samples} samples`);
  const pcm = new Int16Array(samples);
  for (let i = 0; i < samples; i++) pcm[i] = view.getInt16(LIVE_FRAME_HEADER_BYTES + i * 2, true);
  return { source, seq: view.getUint32(2, true), pcm };
}

// ============================================================================
// G.711 mu-law (Twilio Media Streams: 8 kHz, mono, one byte per sample)
// ============================================================================

const MULAW_BIAS = 0x84;
const MULAW_CLIP = 32_635;

/** The 256 decoded values, built once. Marked pure so a bundle that never
 *  touches mu-law (the embed widget, the panel) drops the table entirely. */
const MULAW_DECODE: Int16Array = /* @__PURE__ */ (() => {
  const table = new Int16Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    const exponent = (u >> 4) & 0x07;
    const mantissa = u & 0x0f;
    const magnitude = (((mantissa << 3) + MULAW_BIAS) << exponent) - MULAW_BIAS;
    table[i] = u & 0x80 ? -magnitude : magnitude;
  }
  return table;
})();

export function mulawToPcm16(bytes: Uint8Array): Int16Array {
  const out = new Int16Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = MULAW_DECODE[bytes[i]!]!;
  return out;
}

export function pcm16ToMulaw(pcm: Int16Array): Uint8Array {
  const out = new Uint8Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    let sample = pcm[i]!;
    const sign = sample < 0 ? 0x80 : 0;
    if (sign) sample = -sample;
    if (sample > MULAW_CLIP) sample = MULAW_CLIP;
    sample += MULAW_BIAS;
    let exponent = 7;
    for (let mask = 0x4000; (sample & mask) === 0 && exponent > 0; mask >>= 1) exponent--;
    const mantissa = (sample >> (exponent + 3)) & 0x0f;
    out[i] = ~(sign | (exponent << 4) | mantissa) & 0xff;
  }
  return out;
}

// ============================================================================
// Resampling between Twilio's 8 kHz and the live 16 kHz
// ============================================================================

/**
 * 8 kHz -> 16 kHz by linear interpolation. Stateful: it remembers the last
 * sample of the previous chunk, so a stream of 20 ms chunks is continuous
 * instead of clicking at every chunk boundary.
 */
export class Upsample8to16 {
  private last = 0;

  process(input: Int16Array): Int16Array {
    const out = new Int16Array(input.length * 2);
    let prev = this.last;
    for (let i = 0; i < input.length; i++) {
      const cur = input[i]!;
      out[2 * i] = (prev + cur) >> 1;
      out[2 * i + 1] = cur;
      prev = cur;
    }
    if (input.length > 0) this.last = input[input.length - 1]!;
    return out;
  }
}

/**
 * 16 kHz -> 8 kHz by averaging pairs (a crude low-pass, adequate for speech
 * headed to a phone line that is band-limited to 3.4 kHz anyway). Stateful for
 * odd-length chunks: a leftover sample waits for its partner.
 */
export class Downsample16to8 {
  private carry: number | null = null;

  process(input: Int16Array): Int16Array {
    const total = input.length + (this.carry === null ? 0 : 1);
    const out = new Int16Array(total >> 1);
    let i = 0;
    let o = 0;
    if (this.carry !== null && input.length > 0) {
      out[o++] = (this.carry + input[0]!) >> 1;
      i = 1;
      this.carry = null;
    }
    for (; i + 1 < input.length; i += 2) out[o++] = (input[i]! + input[i + 1]!) >> 1;
    if (i < input.length) this.carry = input[i]!;
    return out;
  }
}

/**
 * 48 kHz float (Web Audio) -> 16 kHz PCM16 by averaging triples, for the
 * browser taps. Stateful like Downsample16to8: an AudioWorklet hands over
 * 128-sample render quanta, which are not multiples of 3, so the one or two
 * samples left over wait for the next block.
 */
export class Decimate48to16 {
  private carry: number[] = [];

  process(input: Float32Array): Int16Array {
    const samples = this.carry.length ? Float32Array.from([...this.carry, ...input]) : input;
    const n = Math.floor(samples.length / 3);
    const out = new Int16Array(n);
    for (let k = 0; k < n; k++) {
      const v = (samples[3 * k]! + samples[3 * k + 1]! + samples[3 * k + 2]!) / 3;
      const c = v > 1 ? 1 : v < -1 ? -1 : v;
      out[k] = c < 0 ? Math.round(c * 0x8000) : Math.round(c * 0x7fff);
    }
    this.carry = Array.from(samples.subarray(n * 3));
    return out;
  }
}

// ============================================================================
// The monitor socket (/ws/monitor/:id) — a person's console <-> the call's DO
// ============================================================================

/** Whether live audio is reaching the DO for this call right now. */
export type LiveAudioStatus =
  /** Asked for, not arrived yet (the caller's page opens /ws/live; Twilio opens its stream). */
  | 'starting'
  | 'flowing'
  /** Cannot be had: `reason` says why (an old page without live support, a phone call with no call sid, ...). */
  | 'unavailable'
  /** The call ended, or nobody is listening any more. */
  | 'stopped';

/** DO -> console. JSON text frames; audio arrives as binary live frames on the same socket. */
export type MonitorServerMsg =
  | {
      t: 'monitor.ready';
      sessionId: string;
      transport: 'webrtc' | 'sip';
      state: import('./index.js').SessionState;
      audio: LiveAudioStatus;
      /** Display name of whoever holds the call, when somebody does. */
      takenOverBy: string | null;
      /** This console's token allows taking over. */
      canTakeOver: boolean;
    }
  | { t: 'audio'; status: LiveAudioStatus; reason?: string }
  | { t: 'state'; state: import('./index.js').SessionState; detail?: string }
  | { t: 'transcript'; who: 'user' | 'assistant'; itemId: string; text: string; final: boolean }
  /**
   * `started`: the AI leg is being ended and this call is now a person's.
   * `refused`: not allowed or not possible (`reason`). `failed`: it was tried
   * and did not complete (`reason`); the AI may still be on the line.
   */
  | { t: 'takeover'; state: 'started' | 'refused' | 'failed'; by?: string; reason?: string }
  | { t: 'error'; code: string; message: string };

/** Console -> DO. Human audio goes up as binary live frames with source Human. */
export type MonitorClientMsg =
  | { t: 'takeover' }
  /** End the call for everyone. Only the person holding the call may. */
  | { t: 'hangup' };
